// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getFirestore, collection, addDoc, getDocs, doc, setDoc, onSnapshot, query, getDoc, Timestamp, orderBy, updateDoc, where, runTransaction } from "firebase/firestore";
import { getStorage } from "firebase/storage";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyB_fkVOdFnMucoZRqb907X0zhC1JOW-MVc",
  authDomain: "tool-lending-app.firebaseapp.com",
  projectId: "tool-lending-app",
  storageBucket: "tool-lending-app.appspot.com",
  messagingSenderId: "181174592766",
  appId: "1:181174592766:web:7eeaf1bc2cd8e91aad31af",
  measurementId: "G-72SE9KTWEK"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Authentication functions
export const signUp = async (email, password, name, address, onboardingCompleted = false) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await setDoc(doc(db, "users", user.uid), {
      name: name,
      email: email,
      address: address,
      onboardingCompleted: onboardingCompleted,
      createdAt: Timestamp.now()
    });

    return user;
  } catch (error) {
    console.error("Error in signUp: ", error);
    throw error;
  }
};

export const logIn = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const logOut = () => {
  return signOut(auth);
};

// Firestore functions
export const createRequest = async (toolId, borrowerId, ownerId, requestedDates, message) => {
  console.log('Creating request with params:', { toolId, borrowerId, ownerId, requestedDates, message });
  try {
    const result = await runTransaction(db, async (transaction) => {
      // Check if documents exist
      const borrowerDocRef = doc(db, "users", borrowerId);
      const ownerDocRef = doc(db, "users", ownerId);
      const toolDocRef = doc(db, "tools", toolId);

      const [borrowerDoc, ownerDoc, toolDoc] = await Promise.all([
        transaction.get(borrowerDocRef),
        transaction.get(ownerDocRef),
        transaction.get(toolDocRef)
      ]);

      if (!borrowerDoc.exists()) {
        throw new Error("Borrower not found");
      }
      if (!ownerDoc.exists()) {
        throw new Error("Owner not found");
      }
      if (!toolDoc.exists()) {
        throw new Error("Tool not found");
      }

      const borrowerData = borrowerDoc.data();
      const ownerData = ownerDoc.data();
      const toolData = toolDoc.data();

      console.log('Creating request document');
      const requestRef = doc(collection(db, "requests"));
      transaction.set(requestRef, {
        toolId: toolId,
        toolName: toolData.name,
        borrowerId: borrowerId,
        borrowerName: borrowerData.name,
        ownerId: ownerId,
        ownerName: ownerData.name,
        requestedDates: requestedDates,
        status: "pending",
        createdAt: Timestamp.now(),
        message: message
      });

      console.log('Creating conversation document');
      const conversationRef = doc(db, "conversations", requestRef.id);
      transaction.set(conversationRef, {
        participants: [borrowerId, ownerId],
        createdAt: Timestamp.now()
      });

      console.log('Adding initial message to conversation');
      const messageRef = doc(collection(db, `conversations/${requestRef.id}/messages`));
      transaction.set(messageRef, {
        senderId: borrowerId,
        senderName: borrowerData.name,
        content: message,
        timestamp: Timestamp.now()
      });

      return requestRef.id;
    });

    console.log("Request creation process completed successfully");
    return result;
  } catch (error) {
    console.error("Error creating request: ", error);
    if (error.code === "permission-denied") {
      console.error("Permission denied. Current user may not have the necessary permissions.");
    }
    throw error;
  }
};

export const getRequests = async (userId) => {
  try {
    console.log('Starting getRequests for userId:', userId);
    const borrowerRequestsQuery = query(collection(db, "requests"), where("borrowerId", "==", userId));
    const ownerRequestsQuery = query(collection(db, "requests"), where("ownerId", "==", userId));
    
    console.log('Fetching borrower and owner requests...');
    const [borrowerRequestsSnapshot, ownerRequestsSnapshot] = await Promise.all([
      getDocs(borrowerRequestsQuery),
      getDocs(ownerRequestsQuery)
    ]);
    console.log('Requests fetched successfully');

    const fetchToolData = async (toolId) => {
      console.log('Fetching tool data for toolId:', toolId);
      const toolDoc = await getDoc(doc(db, "tools", toolId));
      if (!toolDoc.exists()) {
        console.log('Tool document does not exist for toolId:', toolId);
        return null;
      }
      console.log('Tool data fetched successfully for toolId:', toolId);
      return toolDoc.data();
    };

    const processRequests = async (requests, type) => {
      console.log(`Processing ${type} requests...`);
      return Promise.all(requests.map(async (doc) => {
        const requestData = doc.data();
        console.log(`Processing request: ${doc.id}`);
        let toolData;
        try {
          toolData = await fetchToolData(requestData.toolId);
        } catch (error) {
          console.error(`Error fetching tool data for request ${doc.id}:`, error);
          toolData = null;
        }
        console.log('Fetched tool data:', toolData);
        return {
          id: doc.id,
          ...requestData,
          type: type,
          toolImage: toolData ? toolData.images : null
        };
      }));
    };

    console.log('Processing borrower requests...');
    const borrowerRequests = await processRequests(borrowerRequestsSnapshot.docs, 'outbound');
    console.log('Processing owner requests...');
    const ownerRequests = await processRequests(ownerRequestsSnapshot.docs, 'inbound');

    console.log('All requests processed successfully');
    return [...borrowerRequests, ...ownerRequests];
  } catch (error) {
    console.error("Error in getRequests: ", error);
    if (error.code === "permission-denied") {
      console.error("Permission denied. Current user may not have the necessary permissions.");
      console.error("User ID:", userId);
      console.error("Error details:", error.details);
    }
    throw error;
  }
};

export const updateRequestStatus = async (requestId, status) => {
  try {
    const requestRef = doc(db, "requests", requestId);
    await updateDoc(requestRef, { status: status });
    console.log("Request status updated successfully");
  } catch (error) {
    console.error("Error updating request status: ", error);
    throw error;
  }
};

export const createBooking = async (requestId) => {
  try {
    const requestDoc = await getDoc(doc(db, "requests", requestId));
    if (!requestDoc.exists()) {
      throw new Error("Request not found");
    }

    const requestData = requestDoc.data();
    const bookingRef = await addDoc(collection(db, "bookings"), {
      toolId: requestData.toolId,
      borrowerId: requestData.borrowerId,
      ownerId: requestData.ownerId,
      startDate: requestData.requestedDates[0].startDate,
      endDate: requestData.requestedDates[0].endDate,
      status: "confirmed",
      createdAt: Timestamp.now()
    });

    await updateRequestStatus(requestId, "approved");

    console.log("Booking created with ID: ", bookingRef.id);
    return bookingRef.id;
  } catch (error) {
    console.error("Error creating booking: ", error);
    throw error;
  }
};

export const getMessages = async (conversationId) => {
  console.log('Fetching messages for conversation:', conversationId);
  try {
    if (!conversationId) {
      console.error('getMessages: conversationId is undefined or null');
      throw new Error('Invalid conversationId');
    }

    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationDoc = await getDoc(conversationRef);

    if (!conversationDoc.exists()) {
      console.error(`getMessages: Conversation with ID ${conversationId} does not exist`);
      throw new Error('Conversation not found');
    }

    const messagesQuery = query(collection(db, `conversations/${conversationId}/messages`), orderBy("timestamp"));
    const messagesSnapshot = await getDocs(messagesQuery);
    const messages = messagesSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        senderName: data.senderName || 'Unknown User' // Fallback for backward compatibility
      };
    });
    console.log('Fetched messages:', messages);
    return messages;
  } catch (error) {
    console.error("Error fetching messages: ", error);
    throw error;
  }
};

export const subscribeToMessages = (conversationId, callback) => {
  console.log('Setting up message subscription for conversation:', conversationId);
  const messagesQuery = query(collection(db, `conversations/${conversationId}/messages`), orderBy("timestamp"));
  return onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        senderName: data.senderName || 'Unknown User' // Fallback for backward compatibility
      };
    });
    console.log('Received updated messages:', messages);
    callback(messages);
  }, (error) => {
    console.error('Error in message subscription:', error);
  });
};

export const createMessage = async (conversationId, senderId, content, senderName) => {
  try {
    const messageRef = await addDoc(collection(db, `conversations/${conversationId}/messages`), {
      senderId: senderId,
      senderName: senderName,
      content: content,
      timestamp: Timestamp.now()
    });
    console.log("Message sent with ID: ", messageRef.id);
    return messageRef.id;
  } catch (error) {
    console.error("Error sending message: ", error);
    throw error;
  }
};

export const getConversationParticipants = async (conversationId) => {
  try {
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);
    
    if (!conversationDoc.exists()) {
      throw new Error("Conversation not found");
    }
    
    const conversationData = conversationDoc.data();
    return conversationData.participants || [];
  } catch (error) {
    console.error("Error fetching conversation participants: ", error);
    throw error;
  }
};

export const getUserData = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error("User not found");
    }
    
    const userData = userDoc.data();
    return {
      uid: userId,
      displayName: userData.name,
      email: userData.email,
      onboardingCompleted: userData.onboardingCompleted
    };
  } catch (error) {
    console.error("Error fetching user data: ", error);
    throw error;
  }
};

export { auth, db, storage };
